import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { api } from '@lib/api/client';
import type { EnhancedStoreCustomer } from '@marxbiotech/shared-types/medusa';
import { accountKeys } from '@lib/query/options/account';
import { layoutKeys } from '@lib/query/options/layout';

// =============================================================================
// API Response Types
// =============================================================================

interface LoginResponse {
  customer: EnhancedStoreCustomer;
}

interface ResetPasswordResponse {
  success: boolean;
}

interface UpdatePasswordResponse {
  success: boolean;
}

// =============================================================================
// Login
// =============================================================================

interface LoginInput {
  email: string;
  password: string;
}

type UseLoginOptions = Omit<
  UseMutationOptions<EnhancedStoreCustomer, Error, LoginInput>,
  'mutationFn'
>;

export function useLogin(options?: UseLoginOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LoginInput): Promise<EnhancedStoreCustomer> => {
      const response = await api.post<LoginResponse>('/auth/login', data);
      return response.data.customer;
    },
    onSuccess: (...args) => {
      // Invalidate layout and account queries to refresh auth state
      queryClient.invalidateQueries({ queryKey: layoutKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      // Call user-provided onSuccess if exists
      options?.onSuccess?.(...args);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Reset Password (Request)
// =============================================================================

interface ResetPasswordInput {
  email: string;
}

type UseResetPasswordOptions = Omit<
  UseMutationOptions<ResetPasswordResponse, Error, ResetPasswordInput>,
  'mutationFn'
>;

export function useResetPassword(options?: UseResetPasswordOptions) {
  return useMutation({
    mutationFn: async (
      data: ResetPasswordInput
    ): Promise<ResetPasswordResponse> => {
      const response = await api.post<ResetPasswordResponse>(
        '/auth/reset-password',
        data,
        { skipAuthRedirect: true }
      );
      return response.data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Update Password (with token)
// =============================================================================

interface UpdatePasswordInput {
  token: string;
  password: string;
}

type UseUpdatePasswordOptions = Omit<
  UseMutationOptions<UpdatePasswordResponse, Error, UpdatePasswordInput>,
  'mutationFn'
>;

export function useUpdatePassword(options?: UseUpdatePasswordOptions) {
  return useMutation({
    mutationFn: async (
      data: UpdatePasswordInput
    ): Promise<UpdatePasswordResponse> => {
      const response = await api.post<UpdatePasswordResponse>(
        '/auth/update-password',
        data,
        { skipAuthRedirect: true }
      );
      return response.data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Complete Account Setup (with token)
// =============================================================================

interface CompleteAccountSetupInput {
  token: string;
  password: string;
}

interface CompleteAccountSetupResponse {
  success: boolean;
}

type UseCompleteAccountSetupOptions = Omit<
  UseMutationOptions<
    CompleteAccountSetupResponse,
    Error,
    CompleteAccountSetupInput
  >,
  'mutationFn'
>;

export function useCompleteAccountSetup(
  options?: UseCompleteAccountSetupOptions
) {
  return useMutation({
    mutationFn: async (
      data: CompleteAccountSetupInput
    ): Promise<CompleteAccountSetupResponse> => {
      const response = await api.post<CompleteAccountSetupResponse>(
        '/auth/complete-account-setup',
        data,
        { skipAuthRedirect: true }
      );
      return response.data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}
